import { useRef, useState } from 'react';
import { useGameContext } from '@/state/GameContext';
import type { Interpretation } from '@/state/settings';

export function SettingsPanel({ onClose }: { onClose: () => void }) {
  const game = useGameContext();
  const s = game.settings;
  const fileRef = useRef<HTMLInputElement>(null);
  const [importMsg, setImportMsg] = useState<string | null>(null);

  const doExport = async () => {
    const json = await game.exportSave();
    const blob = new Blob([json], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `infinite-crafting-table-save-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const doImport = async (file: File) => {
    try {
      const text = await file.text();
      await game.importSave(text);
      setImportMsg('Imported successfully.');
    } catch (e) {
      setImportMsg('Import failed: ' + (e as Error).message);
    }
  };

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Settings</h3>
        <div className="row">
          <label>Sound</label>
          <button className={`btn ghost${s.sound ? ' primary' : ''}`} onClick={() => game.updateSettings({ sound: !s.sound })}>
            {s.sound ? 'On' : 'Off'}
          </button>
        </div>
        <div className="row">
          <label>Mock AI (offline)</label>
          <button className={`btn ghost${s.mockAi ? ' primary' : ''}`} onClick={() => game.updateSettings({ mockAi: !s.mockAi })}>
            {s.mockAi ? 'On' : 'Off'} {s.mockAi ? '(no API calls)' : ''}
          </button>
          <span className="muted">Server status: {game.serverMock ? 'mock / no key' : 'live key set'}</span>
        </div>
        <div className="row">
          <label>Model</label>
          <input type="text" value={s.model} onChange={(e) => game.updateSettings({ model: e.target.value })} style={{ flex: 1 }} />
        </div>
        <div className="row">
          <label>Interpretation</label>
          <select value={s.interpretation} onChange={(e) => game.updateSettings({ interpretation: e.target.value as Interpretation })}>
            <option value="auto">auto</option>
            <option value="shaped">shaped</option>
            <option value="shapeless">shapeless</option>
          </select>
        </div>
        <div className="row">
          <label>Progression strictness</label>
          <input type="range" min={0} max={2} value={s.strictness} onChange={(e) => game.updateSettings({ strictness: Number(e.target.value) })} />
          <span className="muted">{['lenient', 'moderate', 'strict'][s.strictness]}</span>
        </div>
        <div className="row">
          <label>Reroll cooldown</label>
          <select value={s.rerollCooldownMs} onChange={(e) => game.updateSettings({ rerollCooldownMs: Number(e.target.value) })}>
            <option value={0}>off</option>
            <option value={15000}>15s</option>
            <option value={30000}>30s</option>
            <option value={60000}>60s</option>
          </select>
        </div>
        <div className="row">
          <label>Developer mode</label>
          <button className={`btn ghost${s.devMode ? ' primary' : ''}`} onClick={() => game.updateSettings({ devMode: !s.devMode })}>
            {s.devMode ? 'On' : 'Off'}
          </button>
        </div>

        <h3 style={{ marginTop: 18 }}>Save data</h3>
        <div className="row">
          <button className="btn" onClick={doExport}>Export save (JSON)</button>
          <button className="btn" onClick={() => fileRef.current?.click()}>Import save</button>
          <input ref={fileRef} type="file" accept="application/json" style={{ display: 'none' }} onChange={(e) => e.target.files?.[0] && doImport(e.target.files[0])} />
        </div>
        {importMsg ? <div className="muted">{importMsg}</div> : null}
        <div className="row">
          <button className="btn danger" onClick={async () => { if (confirm('Reset ALL save data? This cannot be undone.')) { await game.resetSave(); onClose(); } }}>
            Reset save
          </button>
          <button className="btn" onClick={async () => { const r = await game.clearCache(); alert(`Cleared ${r.cleared} cached AI recipe(s).`); }}>
            Clear recipe cache
          </button>
        </div>
        <div className="row">
          <button className="btn danger" onClick={async () => { if (confirm(`Remove all ${game.discoveryCount} discovered custom items and their recipe history? Vanilla items are kept.`)) { await game.clearDiscovered(); } }}>
            Clear discovered items
          </button>
          <button className="btn" onClick={async () => { const n = await game.loadSeeds(); alert(`Loaded ${n} example recipes.`); }}>
            Load example recipes
          </button>
        </div>
        <div className="muted" style={{ marginTop: 10 }}>
          Discovered: {game.discoveryCount} &middot; All data is stored locally in your browser (IndexedDB). No account.
        </div>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button className="btn primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
